import * as React from "react";
import { Component } from "react";
import "./observation.scss";
import ObsParam from "./ObsParam";
import LocalStorageService from "../../service/LocalStorageService";
import { ObservationType, ParamType } from "../../api/GrammarApi";
import { EXCLUDED_REASONS, Observation, Param } from "../../api/StudyApi";
import { Collapse, Input, Label, Modal, ModalHeader } from "reactstrap";
import arr from "../../util/ArrayDecorator";
import ModalBody from "reactstrap/lib/ModalBody";
import { AuthManager } from "../auth/AuthContext";
import ObsHistory from "./ObsHistory";
import ObsButtonBar from "./ObsButtonBar";
import i18n from "i18next";
import Tags from "../Tags";

const MANAGED_TAGS = ["UNDER_READ", "SAT_OF_SEARCH", "ABN_OUT_AREA"];

interface ObservationProps {
  studyId: string;
  obs: Observation;

  removeObservation(obs: Observation): void;

  selected: boolean;

  onClickObservation(obs: Observation): void;

  onClickTargetObs(obs: Observation): void;

  authManager: AuthManager;
}

interface ObservationState {
  observationType: ObservationType;
  obs: Observation;
  obsHistoryOpened: boolean;
  collapse: boolean;
}

const userLevels = ["PRIMO", "CLEANER", "ADMIN", "RAD"];

export default class ObservationCard extends Component<ObservationProps, ObservationState> {
  constructor(props: ObservationProps) {
    super(props);
    this.state = this.getStateFromProps(props, null);
  }

  UNSAFE_componentWillReceiveProps(nextProps: ObservationProps) {
    this.setState((prevState) => this.getStateFromProps(nextProps, prevState));
  }

  getStateFromProps(props: ObservationProps, prevState: ObservationState): ObservationState {
    const observationType = LocalStorageService.getObservationType(props.obs.type);

    return {
      observationType,
      obs: props.obs,
      obsHistoryOpened: prevState ? prevState.obsHistoryOpened : false,
      collapse: prevState ? prevState.collapse : false,
    };
  }

  classesFromProps(props: ObservationProps) {
    const classes = ["observation"];

    if (props.selected) classes.push("selected");

    return classes.join(" ");
  }

  getParam(type: string) {
    const obs = this.state.obs;
    return obs ? arr(obs.params).findFirst((e) => e.type === type) : null;
  }

  onChangeObsState(state: string) {
    this.setState((prevState) => {
      const obs = prevState.obs;
      obs.state = state;

      if (obs.state != "excluded") {
        obs.excludedReason = undefined;
      }

      return { obs };
    });
  }

  changeCardinality(cardinality: number) {
    this.state.obs.cardinality = cardinality;
    this.setState({ obs: this.state.obs });
  }

  onParamChange(param: Param) {
    this.setState((prevState) => {
      const obs = prevState.obs;
      const idx = arr(this.state.obs.params).findFirstIndex((elt) => elt.type === param.type);

      if (idx > -1) {
        this.state.obs.params[idx] = param;
      } else {
        this.state.obs.params.push(param);
      }

      return { obs };
    });
  }

  private toggleHistory() {
    this.setState((prevState) => {
      return {
        obsHistoryOpened: !prevState.obsHistoryOpened,
      };
    });
  }

  private toggleCollapse(evt: React.MouseEvent) {
    evt.preventDefault();
    this.setState((prevState) => ({ collapse: !prevState.collapse }));
  }

  private renderButtonTags() {
    return (
      <Tags
        tags={this.state.obs.tags}
        availableTags={MANAGED_TAGS}
        onTagsChanged={(newTags) => this.onTagsChanged(newTags)}
      />
    );
  }

  private onTagsChanged(newTags: Array<string>) {
    this.setState((prevState) => {
      prevState.obs.tags = newTags;
      return { obs: prevState.obs };
    });
  }

  private addValidatedBy() {
    this.setState((prevState) => {
      prevState.obs.validatedBy.push(this.props.authManager.whoAmI().login);
      return prevState;
    });
  }

  private getParamKey(paramType: ParamType): string {
    return this.props.studyId + "-" + this.state.obs.id + "-" + paramType.code;
  }

  private changeUserLevel(level: string) {
    this.setState((prevState) => {
      prevState.obs.level = level;
      return prevState;
    });
  }

  private changeExcludedReason(reason: string) {
    this.setState((prevState) => {
      prevState.obs.excludedReason = reason;
      return prevState;
    });
  }

  render() {
    return (
      <div className={this.classesFromProps(this.props)}>
        {this.state.observationType && (
          <div>
            <div
              className="observation-title cursor-pointer"
              style={{ color: this.props.obs._badge?.color || "" }}
              onClick={(evt) => this.props.onClickObservation(this.state.obs)}
            >
              <div className="row">
                <div className="col-4" onClick={(evt) => this.toggleCollapse(evt)}>
                  <strong>
                    {this.state.observationType.label}
                    {this.props.obs._badge && <span> {this.props.obs._badge.label}</span>}
                  </strong>
                </div>

                <div className="col-8 text-right">
                  <ObsButtonBar
                    obs={this.state.obs}
                    onChangeObsState={(state) => this.onChangeObsState(state)}
                    onChangeCardinality={(card) => this.changeCardinality(card)}
                    onToggleHistory={() => this.toggleHistory()}
                    onAddValidatedBy={() => this.addValidatedBy()}
                    onClickTargetObs={() => this.props.onClickTargetObs(this.state.obs)}
                    onRemoveObs={() => this.props.removeObservation(this.state.obs)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col">
                  {this.state.obs.source && (
                    <div className="small">
                      ({i18n.t("global.source")} : {this.state.obs.source})
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Collapse isOpen={!this.state.collapse}>{this.renderObsDetail()}</Collapse>

            {this.renderHistoryModal()}
          </div>
        )}
      </div>
    );
  }

  private renderObsDetail() {
    return (
      <div className="bg-dark">
        <div className="p-1">{this.renderButtonTags()}</div>

        <div className="row">
          <Label for="level" className="ml-2 col-5">
            Level
          </Label>
          <Input
            onChange={(evt) => this.changeUserLevel(evt.target.value)}
            id="level"
            name="level"
            type="select"
            className="col-6"
            value={this.state.obs.level || undefined}
          >
            {userLevels.map((level) => {
              return (
                <option value={level} key={level}>
                  {level}
                </option>
              );
            })}
          </Input>
        </div>

        {this.state.obs.state == "excluded" && (
          <div className="row">
            <Label for="excludedReason" className="ml-2 col-5">
              {i18n.t("obs.excludedReason")}
            </Label>
            <Input
              onChange={(evt) => this.changeExcludedReason(evt.target.value)}
              id="excludedReason"
              name="excludedReason"
              type="select"
              className="col-6"
              value={this.state.obs.excludedReason}
            >
              <optgroup>
                {EXCLUDED_REASONS.filter((r) => !r.deprecated).map((r) => {
                  return (
                    <option value={r.value} key={r.value}>
                      {r.text}
                    </option>
                  );
                })}
              </optgroup>
              <optgroup label={i18n.t("global.olds")}>
                {EXCLUDED_REASONS.filter((r) => r.deprecated).map((r) => {
                  return (
                    <option value={r.value} key={r.value}>
                      {r.text}
                    </option>
                  );
                })}
              </optgroup>
            </Input>
          </div>
        )}

        <table className="table table-striped table-dark table-sm mb-1">
          <tbody>
            {this.state.observationType.params.map((paramType) => (
              <tr key={this.getParamKey(paramType)}>
                <td>
                  <ObsParam
                    authManager={this.props.authManager}
                    param={this.getParam(paramType.code)}
                    paramType={paramType}
                    onChange={(param) => this.onParamChange(param)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  private renderHistoryModal() {
    return (
      <Modal size="lg" isOpen={this.state.obsHistoryOpened} backdrop={false}>
        <ModalHeader
          style={{ backgroundColor: this.props.obs._badge ? this.props.obs._badge.color : "" }}
          toggle={() => this.toggleHistory()}
        >
          {i18n.t("obs.historyOf")}{" "}
          {this.state.obs._badge && this.state.observationType && (
            <span>
              {this.state.observationType.label} {this.state.obs._badge.label}
            </span>
          )}
        </ModalHeader>

        <ModalBody>{<ObsHistory obs={this.state.obs} onAddValidatedBy={() => this.addValidatedBy()} />}</ModalBody>
      </Modal>
    );
  }
}
